// useShortShareLink.js
import { useRef, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import fetchShortLink from '../action/fetchShortLink.js';
import fetchMeReferral from '../action/fetchMeReferral.js';

import getShortLinkData from '../selector/getShortLinkData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getOperationData from '../selector/getOperationData.js';

import getShareLink from '../resource/getShareLink.js';
import fetchDomainTrackerResource from '../action/fetchDomainTrackerResource.js';
import {
  ANDROID_MINIMUM_VERSION,
  SWAG_ANDROID_PACKAGE_NAME,
  FEATURE_REFERRAL_DOMAIN_TRACKER,
  DOMAIN_TRACKER_LABEL,
  REFERRAL_DOMAIN_TRACKER_TYPE,
  REFERRAL_LINK_ID,
} from '../RemoteConfigKeys.js';

export const ShareType = {
  STORY: 'story',
  SHORT: 'short',
  LIVESTREAM: 'livestream',
  POST: 'post',
};

/**
 * A hook to lazy fetch short share link.
 * You may want to write your processing code inside callback function.
 *
 * @example
 *
 * import useShortShareLink from 'useShortShareLink'
 *
 * const [isFetchingShortShareLink, fetchShortShareLink] = useShortShareLink({
 *  targetId: 'sampleId',
 *  shareType: 'story',
 *  onShortShareLinkFetched: shortShareLink => {
 *    copyTextToClipboard(shortShareLink);
 *  },
 * });
 *
 * @param {Object} param
 * @param {String} param.targetId - A target id to be shared.
 * @param {('story'|'short'|'livestream'|'post')} param.shareType - The type of media.
 * @param {(shortShareLink: string) => void} param.onShortShareLinkFetched - The callback function that takes a shareShortLink parameter.
 * @return {[Boolean, Function]} Return the array that the first element is `isFetching`, the second element is `fetchShortShareLink` respectively.
 *
 */
const useShortShareLink = ({
  targetId,
  shareType,
  onShortShareLinkFetched,
}) => {
  const dispatch = useDispatch();
  const meId = useSelector(state => getMeData(state, 'id'));
  const referralCode = useSelector(state => getMeData(state, 'referralCode'));

  // Domain tracker selectors
  const isReferralDomainTrackerEnabled = useSelector(state =>
    getRemoteConfigData(state, FEATURE_REFERRAL_DOMAIN_TRACKER)
  );
  const domainTrackerLabel = useSelector(state =>
    getRemoteConfigData(state, DOMAIN_TRACKER_LABEL)
  );
  const referralDomainTrackerType = useSelector(state =>
    getRemoteConfigData(state, REFERRAL_DOMAIN_TRACKER_TYPE)
  );
  const fastestLandingUrl = useSelector(state =>
    getOperationData(state, [
      'domainTracker',
      referralDomainTrackerType,
      domainTrackerLabel,
      'fastestResource',
    ])
  );
  const referralLinkId = useSelector(state =>
    getRemoteConfigData(state, REFERRAL_LINK_ID)
  );

  const selectPath = ['me', 'referral', meId];
  const isFetching = useSelector(state =>
    getNetworkingData(state, selectPath, 'isFetching')
  );
  const isFetched = useSelector(state =>
    getNetworkingData(state, selectPath, 'isFetched')
  );
  const shouldFetchReferral = !isFetching && !isFetched;

  useEffect(() => {
    if (meId && shouldFetchReferral && !referralCode) {
      dispatch(fetchMeReferral());
    }
  }, [dispatch, meId, shouldFetchReferral, referralCode]);

  useEffect(() => {
    if (
      isReferralDomainTrackerEnabled &&
      referralDomainTrackerType &&
      (!fastestLandingUrl || domainTrackerLabel)
    ) {
      dispatch(
        fetchDomainTrackerResource({
          resourceType: referralDomainTrackerType,
        })
      );
    }
  }, [
    isReferralDomainTrackerEnabled,
    fastestLandingUrl,
    referralDomainTrackerType,
    domainTrackerLabel,
    dispatch,
  ]);

  const utmQueries = useMemo(() => {
    if (Object.values(ShareType).includes(shareType)) {
      return {
        utm_source: 'share',
        utm_content: `${shareType}_${targetId}`,
        utm_campaign: meId ? `userid_${meId}` : '',
        utm_term: referralCode ? `referral_${referralCode}` : '',
      };
    }
    return undefined;
  }, [meId, targetId, shareType, referralCode]);

  const swagAndroidPackageName = useSelector(state =>
    getRemoteConfigData(state, SWAG_ANDROID_PACKAGE_NAME)
  );
  const androidMinimunVersion = useSelector(state =>
    getRemoteConfigData(state, ANDROID_MINIMUM_VERSION)
  );
  const shareLink =
    shareType === ShareType.LIVESTREAM
      ? getShareLink({
          query: {
            link: typeof window !== 'undefined' ? window?.location?.href : null,
            afl: typeof window !== 'undefined' ? window?.location?.href : null,
            apn: swagAndroidPackageName,
            amv: androidMinimunVersion,
          },
        })
      : getShareLink();

  const applyDomainTracker = useCallback(
    url => {
      const domainTrackerUrl = new URL(url);
      domainTrackerUrl.hostname = new URL(fastestLandingUrl).hostname;
      domainTrackerUrl.searchParams.set('app_distributor', 'referral');
      if (referralLinkId) {
        domainTrackerUrl.searchParams.set('linkId', referralLinkId);
      }
      if (domainTrackerLabel) {
        domainTrackerUrl.searchParams.set('label', domainTrackerLabel);
      }
      return domainTrackerUrl.href;
    },
    [fastestLandingUrl, referralLinkId, domainTrackerLabel]
  );
  const shortShareLink = useSelector(state =>
    getShortLinkData(state, shareLink)
  );
  const shortShareLinkRef = useRef();
  shortShareLinkRef.current = shortShareLink;

  const isFetchingShortShareLink = !!useSelector(state =>
    getNetworkingData(state, ['shortLink', shareLink], 'isFetching')
  );

  const fetchShortShareLink = useCallback(async () => {
    if (shareLink && !shortShareLink) {
      await dispatch(
        fetchShortLink({
          location: shareLink,
          utmQueries,
        })
      );
    }

    let finalUrl = shortShareLinkRef.current || shareLink || location.href;

    if (isReferralDomainTrackerEnabled && fastestLandingUrl && shareLink) {
      finalUrl = applyDomainTracker(finalUrl);
    }

    onShortShareLinkFetched(finalUrl);
  }, [
    dispatch,
    onShortShareLinkFetched,
    shareLink,
    shortShareLink,
    utmQueries,
    isReferralDomainTrackerEnabled,
    fastestLandingUrl,
    applyDomainTracker,
  ]);

  return [isFetchingShortShareLink, fetchShortShareLink];
};

export default useShortShareLink;
